import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Button,
  Field,
  Dropdown,
  Option,
  SpinButton,
  Tag,
  TagGroup,
  Textarea,
  Text,
  Title3,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Card,
  Divider,
  makeStyles,
  tokens,
} from "@fluentui/react-components";

/** Derive a display name from an email local-part, e.g. dona.lim -> "Dona Lim". */
function prettyName(email?: string): string {
  if (!email) return "Approver";
  const local = email.split("@")[0];
  const words = local.split(/[._-]+/).filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1));
  return words.join(" ") || email;
}
import { ArrowRight16Regular, Attach24Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { useApp } from "../context/AppContext";
import { LeaveRequest, LeaveType, LeaveTypeCode } from "../models/leaveModels";
import {
  ATTACHMENT_ACCEPT,
  FILEABLE_LEAVE_CODES,
  MAX_ATTACHMENT_MB,
  MAX_DAYS_PER_REQUEST,
  medCertRequiredFromDays,
} from "../config/leavePolicy";
import {
  bookedDates,
  computeLeaveSplit,
  dateWindowFor,
  makeDateChecker,
  validateLeaveRequest,
} from "../utils/leaveRules";
import { formatDateList, formatLongDate, todayISO } from "../utils/dateUtils";
import { LeaveDatePicker } from "./shared/LeaveDatePicker";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(240px, 320px)",
    gap: tokens.spacingHorizontalXL,
    alignItems: "start",
    "@media (max-width: 760px)": { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  form: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM },
  daysInput: { maxWidth: "160px" },
  dateStep: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  side: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    position: "sticky",
    top: 0,
  },
  summaryCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  bigNum: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: 1,
  },
  sub: { color: tokens.colorNeutralForeground3 },
  routeRow: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalM, flexWrap: "wrap" },
  person: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, minWidth: 0 },
  pinfo: { display: "flex", flexDirection: "column", minWidth: 0 },
  pname: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  prole: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  arrow: { color: tokens.colorNeutralForeground3, flexShrink: 0 },
  submitRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
  },
  attachRow: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS },
  hiddenInput: { display: "none" },
});

export function FileLeave() {
  const s = useStyles();
  const {
    service,
    currentUser,
    employee,
    leaveTypes,
    holidays,
    year,
    refreshSignal,
    triggerRefresh,
    photoUrl,
  } = useApp();

  const [days, setDays] = useState<number | null>(null);
  const [typeCode, setTypeCode] = useState<LeaveTypeCode | "">("");
  const [dates, setDates] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [existing, setExisting] = useState<LeaveRequest[]>([]);
  const [remainingByCode, setRemainingByCode] = useState<Record<string, number>>({});
  const [unselectedNote, setUnselectedNote] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const fileableTypes = useMemo(
    () =>
      FILEABLE_LEAVE_CODES.map((code) => leaveTypes.find((t) => t.code === code)).filter(
        (t): t is LeaveType => !!t,
      ),
    [leaveTypes],
  );
  const selectedType = fileableTypes.find((t) => t.code === typeCode);
  const dayCount = days ?? 0;
  const onProbation = employee?.probationStatus ?? false;
  const approverName = employee?.managerName?.trim() || prettyName(employee?.managerEmail);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      service.getMyLeaveRequests(currentUser.email),
      service.getMyBalances(currentUser.email, year),
    ]).then(([reqs, bals]) => {
      if (cancelled) return;
      setExisting(reqs);
      const map: Record<string, number> = {};
      bals.forEach((b) => (map[b.leaveTypeCode] = b.remaining));
      setRemainingByCode(map);
    });
    return () => {
      cancelled = true;
    };
  }, [service, currentUser.email, year, refreshSignal]);

  const booked = useMemo(() => bookedDates(existing), [existing]);

  const dateWindow = useMemo(
    () =>
      selectedType && dayCount > 0
        ? dateWindowFor(selectedType, dayCount, holidays)
        : undefined,
    [selectedType, dayCount, holidays],
  );

  const checkDate = useMemo(
    () => (dateWindow ? makeDateChecker(dateWindow, holidays, booked) : undefined),
    [dateWindow, holidays, booked],
  );

  // Changing the number of days or the leave type can move the filing window
  // (e.g. 4 -> 5 days of VL goes from 2 weeks' to 1 month's notice). Drop dates
  // that no longer fit.
  useEffect(() => {
    if (!checkDate || !dateWindow) return;
    const fitting = dates.filter((d) => !checkDate(d));
    const kept = fitting.slice(0, dayCount);
    if (kept.length === dates.length) return;
    const notes: string[] = [];
    const outside = dates.length - fitting.length;
    const extra = fitting.length - kept.length;
    if (outside > 0) notes.push(`${outside} date(s) no longer fit the filing window. ${dateWindow.rule.summary}`);
    if (extra > 0) notes.push(`${extra} date(s) removed to match ${dayCount} day(s).`);
    setDates(kept);
    setUnselectedNote(notes.join(" "));
  }, [checkDate, dateWindow, dayCount, dates]);

  const remaining = selectedType ? remainingByCode[selectedType.code] : undefined;
  const hasBalanceRecord = !selectedType?.deductsBalance || remaining !== undefined;
  const split = computeLeaveSplit(dayCount, selectedType, remaining, existing, year);

  const validation = validateLeaveRequest({
    leaveType: selectedType,
    days: dayCount,
    dates,
    reason,
    attachment: file ?? undefined,
    onProbation,
    hasBalanceRecord,
    split,
    checkDate,
  });

  const today = todayISO();
  const calendarMonth = dateWindow && dateWindow.earliest > today ? dateWindow.earliest : today;

  const medCertFrom = selectedType ? medCertRequiredFromDays(selectedType) : undefined;
  const medCertRequired = medCertFrom !== undefined && dayCount >= medCertFrom;

  function clearFile() {
    setFile(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function changeDates(next: string[]) {
    setDates(next);
    setUnselectedNote(null);
  }

  async function submit() {
    if (!selectedType || !validation.ok) return;
    setSubmitting(true);
    setSubmitted(null);
    try {
      const request: LeaveRequest = {
        employee: currentUser.name,
        employeeEmail: currentUser.email,
        leaveTypeId: selectedType.lookupId,
        leaveTypeCode: selectedType.code,
        leaveDates: dates,
        startDate: dates[0],
        endDate: dates[dates.length - 1],
        workingDays: dates.length,
        lwopDays: split.lwopDays,
        reason: reason.trim(),
        status: "Pending",
        managerEmail: employee?.managerEmail ?? "",
        attachmentName: file?.name,
      };
      const created = await service.createLeaveRequest(request, file ?? undefined);
      setSubmitted(created.title ?? "your request");
      // reset
      setDays(null);
      setTypeCode("");
      setDates([]);
      setReason("");
      clearFile();
      setUnselectedNote(null);
      triggerRefresh();
    } catch (err) {
      setSubmitted(null);
      alert(`Could not file request: ${String(err)}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={s.root}>
      <div className={s.form}>
        <Title3>File a leave request</Title3>

        {submitted && (
          <MessageBar intent="success">
            <MessageBarBody>
              <MessageBarTitle>Filed.</MessageBarTitle>
              {submitted} is now pending with your manager.
            </MessageBarBody>
          </MessageBar>
        )}

        <Field
          label="1. Select Number of Calendar Days"
          hint="Count working days only. The days don't have to be consecutive."
          required
        >
          <SpinButton
            className={s.daysInput}
            min={1}
            max={MAX_DAYS_PER_REQUEST}
            value={days}
            placeholder="e.g. 2"
            onChange={(_, data) => {
              let next = data.value;
              if (next === undefined && data.displayValue !== undefined) {
                const parsed = parseInt(data.displayValue, 10);
                next = Number.isNaN(parsed) ? null : parsed;
              }
              setDays(
                next == null
                  ? null
                  : Math.min(MAX_DAYS_PER_REQUEST, Math.max(1, Math.round(next))),
              );
            }}
          />
        </Field>

        <Field label="2. Select Leave Type" required hint={dateWindow?.rule.summary}>
          <Dropdown
            disabled={dayCount === 0}
            placeholder={dayCount === 0 ? "Select the number of days first" : "Select a leave type"}
            value={selectedType ? `${selectedType.code} — ${selectedType.title}` : ""}
            selectedOptions={selectedType ? [selectedType.code] : []}
            onOptionSelect={(_, d) => setTypeCode((d.optionValue as LeaveTypeCode) ?? "")}
          >
            {fileableTypes.map((t) => {
              const blocked = onProbation && !t.availableDuringProbation;
              const label = `${t.code} — ${t.title}${blocked ? " (not during probation)" : ""}`;
              return (
                <Option key={t.code} value={t.code} text={label} disabled={blocked}>
                  {label}
                </Option>
              );
            })}
          </Dropdown>
        </Field>

        <Field
          label={
            dateWindow
              ? `3. Plot Leave Dates on Calendar (${dates.length} of ${dayCount} selected)`
              : "3. Plot Leave Dates on Calendar"
          }
          required
          hint={
            dateWindow
              ? dateWindow.rule.backdateDays > 0
                ? `You can pick dates from ${formatLongDate(dateWindow.earliest)} onward.`
                : `The earliest date you can pick is ${formatLongDate(dateWindow.earliest)}.`
              : "Select the number of days and a leave type to open the calendar."
          }
        >
          <div className={s.dateStep}>
            <LeaveDatePicker
              selected={dates}
              maxSelections={dayCount}
              initialMonth={calendarMonth}
              minDate={dateWindow?.earliest}
              getUnavailableReason={checkDate ?? (() => "Enter days and leave type first")}
              disabled={!dateWindow}
              onChange={changeDates}
            />
            {dates.length > 0 && (
              <TagGroup
                aria-label="Selected dates"
                onDismiss={(_, d) => changeDates(dates.filter((x) => x !== d.value))}
              >
                {dates.map((d) => (
                  <Tag
                    key={d}
                    value={d}
                    size="small"
                    dismissible
                    dismissIcon={{ "aria-label": "Unselect" }}
                  >
                    {formatLongDate(d)}
                  </Tag>
                ))}
              </TagGroup>
            )}
            {unselectedNote && (
              <MessageBar intent="info">
                <MessageBarBody>{unselectedNote}</MessageBarBody>
              </MessageBar>
            )}
          </div>
        </Field>

        <Field label="Reason" required>
          <Textarea
            resize="vertical"
            value={reason}
            placeholder="Briefly describe the reason for this leave"
            onChange={(_, d) => setReason(d.value)}
          />
        </Field>

        <Field
          label={medCertRequired ? "Medical certificate" : "Attachment"}
          required={medCertRequired}
          hint={
            medCertRequired
              ? `Required for ${selectedType?.title.toLowerCase()} of ${medCertFrom} or more days. You can't submit without it. PDF or photo, up to ${MAX_ATTACHMENT_MB} MB.`
              : medCertFrom !== undefined
                ? `Optional for fewer than ${medCertFrom} days. A medical certificate becomes required from ${medCertFrom} days.`
                : `Optional supporting document. PDF or photo, up to ${MAX_ATTACHMENT_MB} MB.`
          }
        >
          <div className={s.attachRow}>
            <input
              ref={fileRef}
              type="file"
              accept={ATTACHMENT_ACCEPT}
              className={s.hiddenInput}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button
              icon={<Attach24Regular />}
              appearance="secondary"
              onClick={() => fileRef.current?.click()}
            >
              {file ? "Change file" : "Choose file"}
            </Button>
            <Text size={200} className={s.sub}>
              {file ? file.name : "No file selected"}
            </Text>
            {file && (
              <Button
                size="small"
                appearance="subtle"
                icon={<Dismiss16Regular />}
                aria-label="Remove attachment"
                onClick={clearFile}
              />
            )}
          </div>
        </Field>

        {validation.errors.map((e, i) => (
          <MessageBar key={`err-${i}`} intent="error">
            <MessageBarBody>{e}</MessageBarBody>
          </MessageBar>
        ))}
        {validation.warnings.map((w, i) => (
          <MessageBar key={`warn-${i}`} intent="warning">
            <MessageBarBody>{w}</MessageBarBody>
          </MessageBar>
        ))}

        <div className={s.submitRow}>
          <Button
            appearance="primary"
            disabled={!validation.ok || submitting}
            onClick={submit}
          >
            {submitting ? (file ? "Uploading and filing…" : "Filing…") : "Submit request"}
          </Button>
          {validation.todo.length > 0 && (
            <Text size={200} className={s.sub}>
              Still needed: {validation.todo.join(" · ")}
            </Text>
          )}
        </div>
      </div>

      <div className={s.side}>
        <Card className={s.summaryCard}>
          <Text weight="semibold">Summary</Text>
          <div>
            <span className={s.bigNum}>{dayCount}</span>{" "}
            <Text className={s.sub}>day(s) requested</Text>
          </div>
          <Text size={200} className={s.sub}>
            {dates.length > 0 ? formatDateList(dates) : "No dates picked yet"}
          </Text>

          <Divider />

          {selectedType?.deductsBalance && hasBalanceRecord ? (
            <>
              <Text weight="semibold">{selectedType.code} balance</Text>
              <Text size={200} className={s.sub}>
                Available: {split.available ?? "—"}
                {split.heldByPending > 0 ? ` (${split.heldByPending} held by pending requests)` : ""}
              </Text>
              {dayCount > 0 && (
                <Text size={200} className={s.sub}>
                  This request: {split.paidDays} paid
                  {split.lwopDays > 0 ? ` + ${split.lwopDays} without pay (LWOP)` : ""} ·
                  after approval: {(split.available ?? 0) - split.paidDays}
                </Text>
              )}
            </>
          ) : selectedType ? (
            <Text size={200} className={s.sub}>
              {selectedType.deductsBalance
                ? `No ${selectedType.code} balance record found for ${year}.`
                : `${selectedType.title} does not deduct from your VL/SL balance.`}
            </Text>
          ) : (
            <Text size={200} className={s.sub}>
              Select a leave type to see the balance impact.
            </Text>
          )}
        </Card>

        <Card>
          <div className={s.routeRow}>
            <div className={s.person}>
              <Avatar
                name={currentUser.name}
                image={photoUrl ? { src: photoUrl } : undefined}
                size={32}
                color="colorful"
              />
              <div className={s.pinfo}>
                <span className={s.pname}>{currentUser.name}</span>
                <span className={s.prole}>Requester{onProbation ? " · on probation" : ""}</span>
              </div>
            </div>
            {employee?.managerEmail ? (
              <>
                <ArrowRight16Regular className={s.arrow} />
                <div className={s.person}>
                  <Avatar name={approverName} size={32} color="colorful" />
                  <div className={s.pinfo}>
                    <span className={s.pname} title={employee.managerEmail}>
                      {approverName}
                    </span>
                    <span className={s.prole}>Approver</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </Card>
      </div>
    </div>
  );
}
